import type { FC, SyntheticEvent } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import ChevronLeftIcon from "@atlaskit/icon/core/chevron-left";
import ChevronRightIcon from "@atlaskit/icon/core/chevron-right";
import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";

export type PaginationProps = {
    /** Array of pages to display */
    pages: number[];
    /** Index of the currently selected page (0-based) */
    selectedIndex?: number;
    /** Aria label for the pagination navigation */
    label?: string;
    /** Aria label for individual page buttons */
    pageLabel?: string;
    /** Aria label for the previous button */
    previousLabel?: string;
    /** Aria label for the next button */
    nextLabel?: string;
    /** Callback when page changes */
    onChange?: (event: SyntheticEvent, page: number) => void;
};

const baseStyles = css({
    display: "inline-flex",
    padding: token("space.400"), // 32px padding on all sides
    alignItems: "center",
    justifyContent: "center",
    fontFamily: wacToken("font.charlie.text"),
    background: wacToken("color.neutral.light0"),
    boxSizing: "border-box"
});

const innerContainerStyles = css({
    display: "flex",
    alignItems: "center",
    gap: token("space.300"), // 24px gap between arrows and page numbers
    boxSizing: "border-box"
});

const pagesContainerStyles = css({
    display: "flex",
    alignItems: "center",
    gap: token("space.200") // 16px spacing between number boxes
});

// @ts-ignore
const _navigatorContainerStyles = css({
    display: "flex",
    alignItems: "center"
});

const pageButtonStyles = css({
    "display": "flex",
    "alignItems": "center",
    "justifyContent": "center",
    "border": "none",
    "background": "transparent",
    "cursor": "pointer",
    "borderRadius": "50%",
    "fontSize": "14px",
    "lineHeight": "20px",
    "fontWeight": "400",
    "width": token("space.400"), // 32px
    "height": token("space.400"), // 32px
    "padding": "0",
    "color": wacToken("color.neutral.light800"),
    "textDecoration": "none",

    // Hover effects only for non-selected pages
    "&:hover:not([aria-current='page'])": {
        color: wacToken("color.primary.blue")
    },

    "&:focus-visible": {
        outline: `${token("space.025")} solid ${wacToken(
            "color.primary.blue"
        )}`, // 2px
        outlineOffset: token("space.025") // 2px
    },

    "&[aria-current='page']": {
        fontWeight: "500",
        backgroundColor: wacToken("color.primary.black"),
        color: wacToken("color.neutral.light0")
    }
});

const navigatorStyles = css({
    "display": "flex",
    "alignItems": "center",
    "justifyContent": "center",
    "border": "none",
    "background": "transparent",
    "cursor": "pointer",
    "borderRadius": token("space.050"), // 4px
    "fontSize": "14px",
    "lineHeight": "20px",
    "fontWeight": "400",
    "width": token("space.400"), // 32px
    "height": token("space.400"), // 32px
    "padding": "0",
    "color": wacToken("color.neutral.light800"),
    "textDecoration": "none",

    "&:focus-visible": {
        outline: `2px solid ${wacToken("color.primary.blue")}`,
        outlineOffset: "2px"
    },

    "&:disabled": {
        cursor: "not-allowed",
        color: wacToken("color.neutral.light400")
    }
});

const ellipsisStyles = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: token("space.400"), // 32px
    height: token("space.400"), // 32px
    color: wacToken("color.neutral.light800"),
    fontSize: "14px",
    lineHeight: "20px",
    fontWeight: "400",
    userSelect: "none"
});

const createPageRange = (pages: number[], selectedIndex: number) => {
    const result: Array<{
        type: "page" | "ellipsis";
        page?: number;
        index?: number;
        key: string;
    }> = [];
    const totalPages = pages.length;
    const current = selectedIndex;

    if (totalPages <= 5) {
        // Show all pages if 5 or fewer
        return pages.map((page, index) => ({
            type: "page" as const,
            page,
            index,
            key: `page-${index}`
        }));
    }

    // Always show first page
    result.push({ type: "page", page: pages[0], index: 0, key: "page-0" });

    if (current <= 2) {
        // Show pages 1, 2, 3, 4, ..., last
        for (let i = 1; i <= 3; i++) {
            if (i < totalPages - 1) {
                result.push({
                    type: "page",
                    page: pages[i],
                    index: i,
                    key: `page-${i}`
                });
            }
        }
        if (totalPages > 5) {
            result.push({ type: "ellipsis", key: "ellipsis-end" });
        }
    } else if (current >= totalPages - 3) {
        if (totalPages > 5) {
            result.push({ type: "ellipsis", key: "ellipsis-start" });
        }
        for (let i = Math.max(1, totalPages - 4); i < totalPages - 1; i++) {
            result.push({
                type: "page",
                page: pages[i],
                index: i,
                key: `page-${i}`
            });
        }
    } else {
        result.push({ type: "ellipsis", key: "ellipsis-start" });
        for (let i = current - 1; i <= current + 1; i++) {
            result.push({
                type: "page",
                page: pages[i],
                index: i,
                key: `page-${i}`
            });
        }
        result.push({ type: "ellipsis", key: "ellipsis-end" });
    }
    if (totalPages > 1) {
        result.push({
            type: "page",
            page: pages[totalPages - 1],
            index: totalPages - 1,
            key: `page-${totalPages - 1}`
        });
    }

    return result;
};

export const Pagination: FC<PaginationProps> = ({
    pages = [],
    selectedIndex = 0,
    label,
    pageLabel,
    previousLabel,
    nextLabel,
    onChange
}) => {
    const { formatMessage } = useIntl();

    // Use provided props or fall back to localized defaults
    const ariaLabels = {
        navigation:
            label ||
            formatMessage({
                id: "wac.design.pagination.navigation.label",
                defaultMessage: "Pagination navigation"
            }),
        page:
            pageLabel ||
            formatMessage({
                id: "wac.design.pagination.page.label",
                defaultMessage: "Page"
            }),
        previous:
            previousLabel ||
            formatMessage({
                id: "wac.design.pagination.previous.label",
                defaultMessage: "Previous page"
            }),
        next:
            nextLabel ||
            formatMessage({
                id: "wac.design.pagination.next.label",
                defaultMessage: "Next page"
            })
    };

    if (pages.length === 0) {
        return null;
    }

    const handlePageClick = (event: SyntheticEvent, pageIndex: number) => {
        if (pageIndex === selectedIndex) {
            return;
        }
        const page = pages[pageIndex];
        if (page !== undefined) {
            onChange?.(event, page);
        }
    };

    const handlePreviousClick = (event: SyntheticEvent) => {
        if (selectedIndex === 0) {
            return;
        }
        const page = pages[selectedIndex - 1];
        if (page !== undefined) {
            onChange?.(event, page);
        }
    };

    const handleNextClick = (event: SyntheticEvent) => {
        if (selectedIndex === pages.length - 1) {
            return;
        }
        const page = pages[selectedIndex + 1];
        if (page !== undefined) {
            onChange?.(event, page);
        }
    };

    const pageRange = createPageRange(pages, selectedIndex);
    const isPreviousDisabled = selectedIndex === 0;
    const isNextDisabled = selectedIndex === pages.length - 1;

    return (
        <nav aria-label={ariaLabels.navigation} css={baseStyles}>
            <div css={innerContainerStyles}>
                {/* Previous Button */}
                <button
                    type="button"
                    css={navigatorStyles}
                    onClick={handlePreviousClick}
                    disabled={isPreviousDisabled}
                    aria-label={ariaLabels.previous}
                >
                    <ChevronLeftIcon label="" />
                </button>

                {/* Page Numbers Container */}
                <div css={pagesContainerStyles}>
                    {pageRange.map((item) => {
                        if (item.type === "ellipsis") {
                            return (
                                <span
                                    key={item.key}
                                    css={ellipsisStyles}
                                    aria-hidden="true"
                                >
                                    ...
                                </span>
                            );
                        }

                        const isSelected = item.index === selectedIndex;
                        const pageNumber = item.page;
                        const pageIndex = item.index;

                        if (
                            pageNumber === undefined ||
                            pageIndex === undefined
                        ) {
                            return null;
                        }

                        return (
                            <button
                                key={item.key}
                                type="button"
                                css={pageButtonStyles}
                                onClick={(event) =>
                                    handlePageClick(event, pageIndex)
                                }
                                aria-current={isSelected ? "page" : undefined}
                                aria-label={`${ariaLabels.page} ${pageNumber}${
                                    isSelected ? ", current page" : ""
                                }`}
                            >
                                {pageNumber}
                            </button>
                        );
                    })}
                </div>

                {/* Next Button */}
                <button
                    type="button"
                    css={navigatorStyles}
                    onClick={handleNextClick}
                    disabled={isNextDisabled}
                    aria-label={ariaLabels.next}
                >
                    <ChevronRightIcon label="" />
                </button>
            </div>
        </nav>
    );
};
